import { ApolloClient, useApolloClient } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GET_EMAIL_INCOMINGS, GET_EMAIL_REQUESTS } from "graphql/query/communication-gql";
import CommunicationConst from "../communication-const";
import { getCommunicationDayKey } from "./communication-date-helper";
import type {
  TCommunicationAuditEntry,
  TCommunicationEquipmentChange,
  TCommunicationFilters,
  TCommunicationFinalChanges,
  TCommunicationQuestion,
  TCommunicationQueueTotals,
  TCommunicationStandUpdates,
  TCommunicationSummary,
  TCommunicationTask,
  TCommunicationTaskFormValues,
} from "./communication-types-helper";

type TCommunicationWhere = {
  column?: string;
  operator?: string;
  value?: unknown;
  AND?: TCommunicationWhere[];
  OR?: TCommunicationWhere[];
};

type TPaginatorData<TItem> = {
  paginatorInfo?: {
    lastPage?: number | null;
  };
  data?: TItem[];
};

type TRawEmailRequest = {
  id?: string | null;
  email_incoming_id?: string | null;
  ee_id?: string | null;
  stand_id?: string | null;
  equipment_id?: string | null;
  flooring_id?: string | null;
  exhibition_name?: string | null;
  company_name?: string | null;
  stand_number?: string | null;
  action?: "ADD" | "REMOVE" | "CHANGE" | "QUESTION" | null;
  item_name?: string | null;
  item_qty?: number | null;
  item_notes?: string | null;
  confidence?: number | null;
  status?: "PENDING" | "APPROVED" | "REJECTED" | "CANCELLED" | null;
  ai_data?: unknown;
  processed_by_id?: string | null;
  processed_at?: string | null;
  notes?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  relExhibitionEvent?: {
    id?: string | null;
    title?: string | null;
    organizer_id?: string | null;
    exhibition_type_id?: string | null;
  } | null;
  relStand?: {
    id?: string | null;
    stand_number?: string | null;
    company_name?: string | null;
  } | null;
  relEquipment?: {
    id?: string | null;
    group_id?: string | null;
    title?: string | null;
    color?: string | null;
  } | null;
  relFlooring?: {
    id?: string | null;
    title?: string | null;
    color?: string | null;
  } | null;
};

type TRawEmailIncoming = {
  id?: string | null;
  subject?: string | null;
  from_email?: string | null;
  from_name?: string | null;
  body_clean?: string | null;
  intent?: string | null;
  status?: string | null;
  parse_error?: string | null;
  parsed_data?: unknown;
  used_token?: number | null;
  received_at?: string | null;
  parsed_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  relEmailChannel?: {
    id?: string | null;
    title?: string | null;
  } | null;
  relEmailRequests?: TRawEmailRequest[] | null;
};

const INCOMING_PAGE_SIZE = 50;
const REQUEST_PAGE_SIZE = 100;
const INCOMING_ID_CHUNK_SIZE = 50;
const REQUEST_FILTER_FIELDS: Array<keyof Pick<
  TCommunicationFilters,
  "event_name" | "company_name" | "stand_number"
>> = [ "event_name", "company_name", "stand_number" ];

const AI_SUMMARY_KEYS = [ "ai_summary", "summary", "short_summary", "overview" ];
const AI_REASON_KEYS = [ "ai_reason", "reason", "explanation", "match_reason" ];

const createFinalChanges = (
  equipmentChanges: TCommunicationEquipmentChange[],
  standUpdates: TCommunicationStandUpdates,
  managerNote: string
): TCommunicationFinalChanges => ({
  equipment_changes: equipmentChanges,
  stand_updates: standUpdates,
  manager_note: managerNote,
});

const mapFormValuesToFinalChanges = (
  values: TCommunicationTaskFormValues
): TCommunicationFinalChanges =>
  createFinalChanges(
    values.equipment_changes || [],
    values.stand_updates || {},
    values.manager_note || ""
  );

const getString = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const pickFirstNonEmpty = (...values: unknown[]): string =>
  values
    .map((value) => getString(value))
    .find(Boolean) || "";

const normalizeConfidence = (value: number | null | undefined): number => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return 0;
  }

  return value > 0 && value <= 1 ? Math.round(value * 100) : Math.round(value);
};

const uniqueValues = (values: string[]): string[] => Array.from(new Set(values.filter(Boolean)));

const chunkArray = <T,>(items: T[], chunkSize: number): T[][] => {
  if (!items.length) {
    return [];
  }

  const chunks: T[][] = [];

  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }

  return chunks;
};

const nestConditions = (
  conditions: Array<TCommunicationWhere | undefined>,
  condition: "AND" | "OR"
): TCommunicationWhere | undefined => {
  const filteredConditions = conditions.filter(Boolean) as TCommunicationWhere[];

  if (!filteredConditions.length) {
    return undefined;
  }

  const [ currentCondition, ...rest ] = filteredConditions;

  if (!rest.length) {
    return currentCondition;
  }

  return {
    ...currentCondition,
    [ condition ]: [ nestConditions(rest, condition) as TCommunicationWhere ],
  };
};

const buildLikeValue = (value: string): string => `%${value.trim()}%`;

const buildRequestWhere = (filters: TCommunicationFilters): TCommunicationWhere | undefined =>
  nestConditions(
    [
      filters.event_name
        ? {
          column: "EXHIBITION_NAME",
          operator: "LIKE",
          value: buildLikeValue(filters.event_name),
        }
        : undefined,
      filters.company_name
        ? {
          column: "COMPANY_NAME",
          operator: "LIKE",
          value: buildLikeValue(filters.company_name),
        }
        : undefined,
      filters.stand_number
        ? {
          column: "STAND_NUMBER",
          operator: "LIKE",
          value: buildLikeValue(filters.stand_number),
        }
        : undefined,
    ],
    "AND"
  );

const buildIncomingWhere = (
  filters: TCommunicationFilters,
  incomingIds?: string[]
): TCommunicationWhere | undefined => {
  const conditions: Array<TCommunicationWhere | undefined> = [];

  if (incomingIds?.length) {
    conditions.push({
      column: "ID",
      operator: "IN",
      value: incomingIds,
    });
  }

  if (filters.sender.trim()) {
    conditions.push(
      nestConditions(
        [
          {
            column: "FROM_EMAIL",
            operator: "LIKE",
            value: buildLikeValue(filters.sender),
          },
          {
            column: "FROM_NAME",
            operator: "LIKE",
            value: buildLikeValue(filters.sender),
          },
        ],
        "OR"
      )
    );
  }

  if (filters.received_date) {
    conditions.push({
      column: "RECEIVED_AT",
      operator: "GTE",
      value: `${filters.received_date}T00:00:00`,
    });
    conditions.push({
      column: "RECEIVED_AT",
      operator: "LTE",
      value: `${filters.received_date}T23:59:59`,
    });
  }

  return nestConditions(conditions, "AND");
};

const hasRequestLevelFilters = (filters: TCommunicationFilters): boolean =>
  REQUEST_FILTER_FIELDS.some((field) => Boolean(filters[ field ]?.trim()));

const readNestedString = (value: unknown, keys: string[], depth = 0): string => {
  if (depth > 3 || value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string") {
    return value.trim();
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const nestedValue = readNestedString(item, keys, depth + 1);

      if (nestedValue) {
        return nestedValue;
      }
    }

    return "";
  }

  if (typeof value !== "object") {
    return "";
  }

  const record = value as Record<string, unknown>;

  for (const key of keys) {
    const directValue = record[ key ];

    if (typeof directValue === "string" && directValue.trim()) {
      return directValue.trim();
    }
  }

  for (const nestedValue of Object.values(record)) {
    const extractedValue = readNestedString(nestedValue, keys, depth + 1);

    if (extractedValue) {
      return extractedValue;
    }
  }

  return "";
};

const getLatestDate = (values: Array<string | null | undefined>): string | undefined => {
  const normalizedValues = uniqueValues(values.map((value) => getString(value)));

  if (!normalizedValues.length) {
    return undefined;
  }

  return normalizedValues.sort(
    (left, right) => new Date(right).getTime() - new Date(left).getTime()
  )[ 0 ];
};

const getTaskStatusFromValues = (
  values: Pick<TCommunicationTaskFormValues, "event_name" | "company_name" | "stand_number">,
  isManagerApproved = false
): TCommunicationTask["status"] => {
  if (isManagerApproved) {
    return CommunicationConst.statuses.APPROVED;
  }

  return values.event_name?.trim() && values.company_name?.trim() && values.stand_number?.trim()
    ? CommunicationConst.statuses.PENDING
    : CommunicationConst.statuses.UNIDENTIFIED;
};

const patchTaskWithFormValues = (
  task: TCommunicationTask,
  values: TCommunicationTaskFormValues
): TCommunicationTask => {
  const finalChanges = mapFormValuesToFinalChanges(values);

  return {
    ...task,
    event_name: getString(values.event_name),
    company_name: getString(values.company_name),
    stand_number: getString(values.stand_number),
    organizer_id: values.organizer_id || undefined,
    exhibition_type_id: values.exhibition_type_id || undefined,
    questions: values.questions || [],
    final_changes: finalChanges,
    status: getTaskStatusFromValues(values),
    updated_at: new Date().toISOString(),
  };
};

const getPrimaryRequest = (requests: TRawEmailRequest[]): TRawEmailRequest | undefined =>
  [ ...requests ].sort((left, right) => {
    const leftCompleteness = [
      pickFirstNonEmpty(left.exhibition_name, left.relExhibitionEvent?.title),
      pickFirstNonEmpty(left.company_name, left.relStand?.company_name),
      pickFirstNonEmpty(left.stand_number, left.relStand?.stand_number),
    ].filter(Boolean).length;
    const rightCompleteness = [
      pickFirstNonEmpty(right.exhibition_name, right.relExhibitionEvent?.title),
      pickFirstNonEmpty(right.company_name, right.relStand?.company_name),
      pickFirstNonEmpty(right.stand_number, right.relStand?.stand_number),
    ].filter(Boolean).length;

    if (rightCompleteness !== leftCompleteness) {
      return rightCompleteness - leftCompleteness;
    }

    return normalizeConfidence(right.confidence) - normalizeConfidence(left.confidence);
  })[ 0 ];

const getPrimaryFlooringRequest = (requests: TRawEmailRequest[]): TRawEmailRequest | undefined =>
  requests.find((request) => getString(request.flooring_id) || getString(request.relFlooring?.id));

const mapQuestionStatus = (
  status: TRawEmailRequest["status"]
): TCommunicationQuestion["status"] => (status === "APPROVED" ? "reviewed" : "open");

const toLowercaseAction = (
  action: TRawEmailRequest["action"]
): TCommunicationEquipmentChange["action"] => {
  if (action === "REMOVE") {
    return "remove";
  }

  if (action === "CHANGE") {
    return "change";
  }

  return "add";
};

const extractAiSummary = (incoming: TRawEmailIncoming, requests: TRawEmailRequest[]): string =>
  readNestedString(incoming.parsed_data, AI_SUMMARY_KEYS)
  || requests.map((request) => readNestedString(request.ai_data, AI_SUMMARY_KEYS)).find(Boolean)
  || "";

const extractAiReason = (incoming: TRawEmailIncoming, requests: TRawEmailRequest[]): string =>
  getString(incoming.parse_error)
  || readNestedString(incoming.parsed_data, AI_REASON_KEYS)
  || requests.map((request) => readNestedString(request.ai_data, AI_REASON_KEYS)).find(Boolean)
  || "";

const mapIncomingToTask = (incoming: TRawEmailIncoming): TCommunicationTask => {
  const requests = incoming.relEmailRequests || [];
  const primaryRequest = getPrimaryRequest(requests);
  const flooringRequest = getPrimaryFlooringRequest(requests);
  const eventName = pickFirstNonEmpty(
    primaryRequest?.exhibition_name,
    primaryRequest?.relExhibitionEvent?.title
  );
  const companyName = pickFirstNonEmpty(
    primaryRequest?.company_name,
    primaryRequest?.relStand?.company_name
  );
  const standNumber = pickFirstNonEmpty(
    primaryRequest?.stand_number,
    primaryRequest?.relStand?.stand_number
  );
  const questions = requests
    .filter((request) => request.action === "QUESTION")
    .map((request) => ({
      id: pickFirstNonEmpty(request.id, `question-${incoming.id}`),
      text: pickFirstNonEmpty(request.item_notes, request.item_name, request.notes),
      status: mapQuestionStatus(request.status),
    }))
    .filter((question) => question.text);
  const equipmentChanges = requests
    .filter((request) => request.action && request.action !== "QUESTION")
    .map((request): TCommunicationEquipmentChange => {
      const suggestedEquipmentGroupId = pickFirstNonEmpty(request.relEquipment?.group_id) || undefined;

      return {
        id: pickFirstNonEmpty(request.id, `change-${incoming.id}`),
        action: toLowercaseAction(request.action),
        item_label: pickFirstNonEmpty(request.item_name, request.relEquipment?.title),
        equipment_title: pickFirstNonEmpty(request.relEquipment?.title) || undefined,
        qty: request.item_qty || 1,
        color: pickFirstNonEmpty(request.relEquipment?.color, request.relFlooring?.color) || null,
        equipment_id: pickFirstNonEmpty(request.equipment_id, request.relEquipment?.id) || undefined,
        equipment_group_id: suggestedEquipmentGroupId,
        suggested_equipment_group_id: suggestedEquipmentGroupId,
        suggestion_status: suggestedEquipmentGroupId ? "pending" : undefined,
      };
    })
    .filter((change) => change.item_label);
  const standUpdates: TCommunicationStandUpdates = {
    flooring_id: pickFirstNonEmpty(
      flooringRequest?.flooring_id,
      flooringRequest?.relFlooring?.id
    ) || undefined,
    flooring_color: pickFirstNonEmpty(flooringRequest?.relFlooring?.color) || undefined,
  };
  const managerNote = uniqueValues(
    requests.map((request) => getString(request.notes)).filter(Boolean)
  ).join("\n\n");
  const finalChanges = createFinalChanges(equipmentChanges, standUpdates, managerNote);
  const hasRequiredMatch = Boolean(eventName && companyName && standNumber);
  const parserProcessedAt =
    getLatestDate([
      incoming.parsed_at,
      ...requests.map((request) => pickFirstNonEmpty(request.processed_at)),
    ]);

  return {
    id: pickFirstNonEmpty(incoming.id),
    source_email_id: pickFirstNonEmpty(incoming.id),
    sender: pickFirstNonEmpty(incoming.from_name)
      ? `${pickFirstNonEmpty(incoming.from_name)} <${pickFirstNonEmpty(incoming.from_email)}>`
      : pickFirstNonEmpty(incoming.from_email),
    sender_email: pickFirstNonEmpty(incoming.from_email) || undefined,
    subject: pickFirstNonEmpty(incoming.subject),
    event_name: eventName,
    event_id: pickFirstNonEmpty(primaryRequest?.ee_id) || undefined,
    stand_number: standNumber,
    stand_id: pickFirstNonEmpty(primaryRequest?.stand_id) || undefined,
    company_name: companyName,
    status:
      !requests.length || !hasRequiredMatch
        ? CommunicationConst.statuses.UNIDENTIFIED
        : CommunicationConst.statuses.PENDING,
    confidence: Math.max(0, ...requests.map((request) => normalizeConfidence(request.confidence))),
    original_body: pickFirstNonEmpty(incoming.body_clean),
    ai_summary: extractAiSummary(incoming, requests),
    ai_reason: extractAiReason(incoming, requests),
    questions,
    organizer_id: pickFirstNonEmpty(primaryRequest?.relExhibitionEvent?.organizer_id) || undefined,
    exhibition_type_id:
      pickFirstNonEmpty(primaryRequest?.relExhibitionEvent?.exhibition_type_id) || undefined,
    extracted_changes: finalChanges,
    final_changes: finalChanges,
    received_at: pickFirstNonEmpty(incoming.received_at, incoming.created_at),
    parser_processed_at: parserProcessedAt,
    approved_at: undefined,
    approved_by: undefined,
    updated_at:
      getLatestDate([
        incoming.updated_at,
        incoming.parsed_at,
        incoming.received_at,
        ...requests.map((request) =>
          pickFirstNonEmpty(request.processed_at, request.updated_at, request.created_at)
        ),
      ]) || pickFirstNonEmpty(incoming.received_at),
  };
};

const mapTasksToAuditEntries = (tasks: TCommunicationTask[]): TCommunicationAuditEntry[] =>
  tasks
    .filter((task) => task.status === CommunicationConst.statuses.APPROVED)
    .map((task) => ({
      id: `audit-${task.id}`,
      task_id: task.id,
      source_email_id: task.source_email_id,
      before_changes: task.extracted_changes,
      after_changes: task.final_changes,
      before_questions: task.questions,
      after_questions: task.questions,
      approved_at: task.approved_at || task.updated_at || task.received_at,
      approved_by: task.approved_by || "",
      email_snapshot: {
        sender: task.sender,
        subject: task.subject,
        original_body: task.original_body,
        event_name: task.event_name,
        company_name: task.company_name,
        stand_number: task.stand_number,
      },
    }));

export const getCommunicationSummary = (tasks: TCommunicationTask[]): TCommunicationSummary => {
  const todayKey = getCommunicationDayKey();
  const queueTotals: TCommunicationQueueTotals = {
    inbox: tasks.filter((task) => task.status !== CommunicationConst.statuses.APPROVED).length,
    pending: tasks.filter((task) => task.status === CommunicationConst.statuses.PENDING).length,
    unidentified: tasks.filter((task) => task.status === CommunicationConst.statuses.UNIDENTIFIED)
      .length,
    parserProcessedToday: tasks.filter(
      (task) =>
        Boolean(task.parser_processed_at)
        && getCommunicationDayKey(task.parser_processed_at) === todayKey
    ).length,
    managerApprovedToday: tasks.filter(
      (task) =>
        Boolean(task.approved_at)
        && getCommunicationDayKey(task.approved_at) === todayKey
    ).length,
  };

  return { queueTotals };
};

const fetchPaginatedData = async <TItem,>(
  client: ApolloClient<object>,
  query: any,
  model: string,
  pageSize: number,
  baseVariables: Record<string, unknown>,
  isStale: () => boolean
): Promise<TItem[] | undefined> => {
  const aggregatedItems: TItem[] = [];
  let currentPage = 1;
  let lastPage = 1;

  do {
    if (isStale()) {
      return undefined;
    }

    const response = await client.query<{ [ key: string ]: TPaginatorData<TItem> }>({
      query,
      variables: {
        ...baseVariables,
        first: pageSize,
        page: currentPage,
      },
      fetchPolicy: "network-only",
    });

    const payload = response.data?.[ model ];
    aggregatedItems.push(...(payload?.data || []));
    lastPage = payload?.paginatorInfo?.lastPage || currentPage;
    currentPage += 1;
  } while (currentPage <= lastPage);

  return aggregatedItems;
};

export const useCommunicationStore = (
  filters: TCommunicationFilters,
  currentManagerName?: string
) => {
  const client = useApolloClient();
  const requestIdRef = useRef(0);
  const [ tasks, setTasks ] = useState<TCommunicationTask[]>([]);
  const [ loading, setLoading ] = useState<boolean>(true);
  const [ error, setError ] = useState<string>();

  const loadTasks = useCallback(async () => {
    const currentRequestId = ++requestIdRef.current;
    const isStale = () => currentRequestId !== requestIdRef.current;

    setLoading(true);
    setError(undefined);

    try {
      const requestWhere = buildRequestWhere(filters);
      const incomingWhere = buildIncomingWhere(filters);
      let incomings: TRawEmailIncoming[] = [];

      if (hasRequestLevelFilters(filters) && requestWhere) {
        const matchedRequests = await fetchPaginatedData<TRawEmailRequest>(
          client,
          GET_EMAIL_REQUESTS,
          "emailRequests",
          REQUEST_PAGE_SIZE,
          {
            where: requestWhere,
            orderBy: [ { column: "ID", order: "DESC" } ],
          },
          isStale
        );

        if (isStale()) {
          return;
        }

        const incomingIds = uniqueValues(
          (matchedRequests || []).map((request) => getString(request.email_incoming_id))
        );

        if (!incomingIds.length) {
          setTasks([]);
          setLoading(false);
          return;
        }

        const incomingIdChunks = chunkArray(incomingIds, INCOMING_ID_CHUNK_SIZE);
        const incomingResults: TRawEmailIncoming[] = [];

        for (const incomingIdChunk of incomingIdChunks) {
          if (isStale()) {
            return;
          }

          const response = await client.query<{ emailIncomings: TPaginatorData<TRawEmailIncoming> }>({
            query: GET_EMAIL_INCOMINGS,
            variables: {
              first: incomingIdChunk.length,
              page: 1,
              where: buildIncomingWhere(filters, incomingIdChunk),
              orderBy: [ { column: "RECEIVED_AT", order: "DESC" } ],
            },
            fetchPolicy: "network-only",
          });

          incomingResults.push(...(response.data?.emailIncomings?.data || []));
        }

        incomings = incomingResults;
      } else {
        incomings = (
          await fetchPaginatedData<TRawEmailIncoming>(
            client,
            GET_EMAIL_INCOMINGS,
            "emailIncomings",
            INCOMING_PAGE_SIZE,
            {
              where: incomingWhere,
              orderBy: [ { column: "RECEIVED_AT", order: "DESC" } ],
            },
            isStale
          )
        ) || [];
      }

      if (isStale()) {
        return;
      }

      const nextTasks = Array.from(
        incomings
          .reduce<Map<string, TCommunicationTask>>((acc, incoming) => {
            const incomingId = getString(incoming.id);

            if (!incomingId) {
              return acc;
            }

            acc.set(incomingId, mapIncomingToTask(incoming));
            return acc;
          }, new Map<string, TCommunicationTask>())
          .values()
      ).sort((left, right) => new Date(right.received_at).getTime() - new Date(left.received_at).getTime());

      setTasks(nextTasks);
      setLoading(false);
    } catch (nextError) {
      if (isStale()) {
        return;
      }

      setTasks([]);
      setError(nextError instanceof Error ? nextError.message : "Failed to load communications");
      setLoading(false);
    }
  }, [
    client,
    filters.company_name,
    filters.event_name,
    filters.received_date,
    filters.sender,
    filters.stand_number,
  ]);

  useEffect(() => {
    loadTasks();

    return () => {
      requestIdRef.current += 1;
    };
  }, [ loadTasks ]);

  const auditEntries = useMemo(() => mapTasksToAuditEntries(tasks), [ tasks ]);
  const summary = useMemo(() => getCommunicationSummary(tasks), [ tasks ]);
  const saveTask = useCallback((taskId: string, values: TCommunicationTaskFormValues) => {
    setTasks((currentTasks) =>
      currentTasks.map((task) =>
        task.id === taskId
          ? patchTaskWithFormValues(task, values)
          : task
      )
    );
  }, []);
  const approveTask = useCallback(
    (taskId: string, values: TCommunicationTaskFormValues) => {
      const approvedAt = new Date().toISOString();
      const approvedBy = getString(currentManagerName) || "Current manager";

      setTasks((currentTasks) =>
        currentTasks.map((task) => {
          if (task.id !== taskId) {
            return task;
          }

          const nextTask = patchTaskWithFormValues(task, values);

          return {
            ...nextTask,
            status: getTaskStatusFromValues(values, true),
            approved_at: approvedAt,
            approved_by: approvedBy,
            updated_at: approvedAt,
          };
        })
      );
    },
    [ currentManagerName ]
  );

  return {
    tasks,
    auditEntries,
    saveTask,
    approveTask,
    summary,
    loading,
    error,
  };
};
